import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  useTheme,
  Skeleton,
  Button,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Star,
  EmojiEvents,
  Lightbulb,
  Groups,
  Celebration,
  WorkspacePremium,
  ArrowForward,
  Add
} from '@mui/icons-material';

interface Recognition {
  id: number;
  employee_id: string;
  employee_name: string;
  recognition_type: 'star_performer' | 'team_player' | 'innovation' | 'milestone' | 'customer_champion' | 'custom';
  title: string;
  description?: string;
  badge_icon?: string;
  recognized_by_name?: string;
  created_at: string;
}

interface WallOfFameWidgetProps {
  recognitions: Recognition[];
  loading?: boolean;
  onViewAll?: () => void;
  onAddRecognition?: () => void;
  canAddRecognition?: boolean;
  maxItems?: number;
}

const WallOfFameWidget: React.FC<WallOfFameWidgetProps> = ({
  recognitions,
  loading = false,
  onViewAll,
  onAddRecognition,
  canAddRecognition = false,
  maxItems = 4
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const getRecognitionIcon = (type: Recognition['recognition_type']) => {
    switch (type) {
      case 'star_performer':
        return <Star sx={{ color: '#FFD700' }} />;
      case 'team_player':
        return <Groups sx={{ color: '#4CAF50' }} />;
      case 'innovation':
        return <Lightbulb sx={{ color: '#FF9800' }} />;
      case 'milestone':
        return <EmojiEvents sx={{ color: '#9C27B0' }} />;
      case 'customer_champion':
        return <WorkspacePremium sx={{ color: '#2196F3' }} />;
      default:
        return <Celebration sx={{ color: '#E91E63' }} />;
    }
  };

  const getRecognitionColor = (type: Recognition['recognition_type']) => {
    switch (type) {
      case 'star_performer':
        return { bg: '#FFD70020', border: '#FFD70040', text: '#B8860B' };
      case 'team_player':
        return { bg: '#4CAF5020', border: '#4CAF5040', text: '#2E7D32' };
      case 'innovation':
        return { bg: '#FF980020', border: '#FF980040', text: '#E65100' };
      case 'milestone':
        return { bg: '#9C27B020', border: '#9C27B040', text: '#7B1FA2' };
      case 'customer_champion':
        return { bg: '#2196F320', border: '#2196F340', text: '#1565C0' };
      default:
        return { bg: '#E91E6320', border: '#E91E6340', text: '#C2185B' };
    }
  };

  const formatRecognitionType = (type: Recognition['recognition_type']) => {
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getInitials = (name: string) => {
    const names = name.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  const displayedRecognitions = recognitions.slice(0, maxItems);

  return (
    <Card
      sx={{
        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : theme.palette.background.paper,
        borderRadius: 2,
        border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : theme.palette.divider}`,
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <CardContent sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : theme.palette.divider}`
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmojiEvents sx={{ color: '#FFD700' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Wall of Fame
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {canAddRecognition && onAddRecognition && (
              <Tooltip title="Give Recognition">
                <IconButton size="small" onClick={onAddRecognition} color="primary">
                  <Add />
                </IconButton>
              </Tooltip>
            )}
            {onViewAll && (
              <Button
                size="small"
                endIcon={<ArrowForward />}
                onClick={onViewAll}
                sx={{ textTransform: 'none' }}
              >
                View All
              </Button>
            )}
          </Box>
        </Box>

        {/* Recognitions Grid */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {loading ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
              {[1, 2, 3, 4].map(i => (
                <Box key={i} sx={{ p: 2, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <Skeleton variant="circular" width={40} height={40} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="80%" />
                      <Skeleton variant="text" width="50%" />
                    </Box>
                  </Box>
                  <Skeleton variant="text" width="60%" />
                </Box>
              ))}
            </Box>
          ) : displayedRecognitions.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
                px: 2
              }}
            >
              <EmojiEvents sx={{ fontSize: 48, color: theme.palette.text.disabled, mb: 1 }} />
              <Typography variant="body1" color="text.secondary">
                No recognitions yet
              </Typography>
              {canAddRecognition && (
                <Button
                  startIcon={<Add />}
                  onClick={onAddRecognition}
                  sx={{ mt: 2, textTransform: 'none' }}
                >
                  Give First Recognition
                </Button>
              )}
            </Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 2
              }}
            >
              {displayedRecognitions.map((recognition) => {
                const colors = getRecognitionColor(recognition.recognition_type);

                return (
                  <Box
                    key={recognition.id}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: colors.bg,
                      border: `1px solid ${colors.border}`,
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 4px 12px ${colors.border}`
                      }
                    }}
                  >
                    {/* Employee Info */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                      <Avatar
                        sx={{
                          width: 44,
                          height: 44,
                          bgcolor: theme.palette.primary.main,
                          fontSize: '1rem',
                          fontWeight: 600
                        }}
                      >
                        {getInitials(recognition.employee_name)}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.text.primary
                          }}
                          noWrap
                        >
                          {recognition.employee_name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: theme.palette.text.secondary }}
                        >
                          {formatTimeAgo(recognition.created_at)}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(255, 255, 255, 0.8)'
                        }}
                      >
                        {getRecognitionIcon(recognition.recognition_type)}
                      </Box>
                    </Box>

                    {/* Recognition Details */}
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: theme.palette.text.primary,
                        mb: 0.5
                      }}
                      noWrap
                    >
                      {recognition.title}
                    </Typography>

                    <Chip
                      size="small"
                      label={formatRecognitionType(recognition.recognition_type)}
                      sx={{
                        height: 22,
                        fontSize: '0.7rem',
                        fontWeight: 500,
                        backgroundColor: 'rgba(255, 255, 255, 0.5)',
                        color: colors.text,
                        border: `1px solid ${colors.border}`
                      }}
                    />

                    {recognition.recognized_by_name && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mt: 1,
                          color: theme.palette.text.secondary,
                          fontStyle: 'italic'
                        }}
                      >
                        by {recognition.recognized_by_name}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        {/* Footer - if more recognitions exist */}
        {recognitions.length > maxItems && (
          <Box
            sx={{
              p: 1.5,
              textAlign: 'center',
              borderTop: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : theme.palette.divider}`
            }}
          >
            <Typography variant="caption" color="text.secondary">
              +{recognitions.length - maxItems} more recognitions
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default WallOfFameWidget;
